import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from '@/components/ui/input';
import { isoToMdy, formatMdyInput, parseMdy, dobInWords, incompleteDobHint } from '@/utils/dobText';

/**
 * Typed date of birth (MM/DD/YYYY, number keypad). `value`/`onChange` speak
 * ISO 'YYYY-MM-DD'; onChange gets '' until the date is complete and real, so
 * the form can't continue with half a date. Why not <input type="date">: see
 * src/utils/dobText.js.
 */
export default function DobInput({ value, onChange, onIncompleteChange, showHint = false, className, id, 'aria-label': ariaLabel }) {
  const [text, setText] = useState(() => isoToMdy(value));
  // Set when the customer leaves the field; an unfinished date then says why
  // it can't be used. Typing again hides the note until they leave again.
  const [left, setLeft] = useState(false);
  // The last ISO value this field reported. A different `value` arriving from
  // the parent (a saved traveler picked) replaces what's typed; our own echo
  // of a keystroke does not, so typing is never overwritten mid-entry.
  const reported = useRef(value || '');

  useEffect(() => {
    const next = value || '';
    if (next !== reported.current) {
      reported.current = next;
      setText(isoToMdy(next));
    }
  }, [value]);

  const show = (nextText) => {
    setText(nextText);
    const { iso } = parseMdy(nextText);
    if (iso !== reported.current) {
      reported.current = iso;
      onChange(iso);
    }
  };

  // Typing at the end (and pastes / autofill, which leave the caret at the
  // end) gets slashes and loose shapes tidied as it arrives. An edit inside
  // a finished date is left exactly as typed, caret and all: tidying
  // '06/3/1980' (day 23 selected, '3' typed on the way to 30) would pad it
  // to 06/03 and jump the caret past the day. It reads as incomplete until
  // the day is whole again, and leaving the field tidies whatever is left.
  const handleChange = (e) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart;
    const atEnd = caret == null || caret >= raw.length;
    setLeft(false);
    show(atEnd ? formatMdyInput(raw) : raw.replace(/[^\d/]/g, '').slice(0, 10));
  };

  // A whole date pasted in any shape ('June 23, 1980', '1980-06-23') replaces
  // the field; maxLength would otherwise cut it before it could be read. A
  // fragment ('1981' over a selected year) pastes the normal way, into the
  // selection, and goes through handleChange like typing.
  const handlePaste = (e) => {
    const whole = formatMdyInput(e.clipboardData?.getData('text') || '');
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(whole)) return;
    e.preventDefault();
    setLeft(false);
    show(whole);
  };

  const handleBlur = () => {
    const tidy = formatMdyInput(text);
    if (tidy !== text) show(tidy);
    setLeft(true);
  };

  const { iso, error: invalid } = parseMdy(text);
  // Something typed that isn't a whole, real date yet. A form where the date
  // is optional (Profile) uses this to hold Save, so a half-typed correction
  // can't quietly save the traveler with no date of birth.
  const incomplete = text !== '' && !iso;
  const onIncompleteRef = useRef(onIncompleteChange);
  onIncompleteRef.current = onIncompleteChange;
  useEffect(() => {
    onIncompleteRef.current?.(incomplete);
  }, [incomplete]);
  const error = invalid || ((left || showHint) && incomplete ? incompleteDobHint(text) : '');

  return (
    <>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        placeholder="MM/DD/YYYY"
        maxLength={10}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handlePaste}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        className={className}
      />
      {iso && <p className="text-[11px] text-white/50 pl-1" data-testid="dob-in-words">{dobInWords(iso)}</p>}
      {error && <p className="text-[11px] text-red-300 pl-1" role="alert">{error}</p>}
    </>
  );
}

DobInput.propTypes = {
  /** ISO 'YYYY-MM-DD', or '' when there's no complete date yet. */
  value: PropTypes.string,
  /** Called with ISO 'YYYY-MM-DD', or '' while the typed date is incomplete or not real. */
  onChange: PropTypes.func.isRequired,
  /** Called with true while the field holds text that isn't a whole, real date. */
  onIncompleteChange: PropTypes.func,
  /** Show why an unfinished date can't be used without waiting for the customer to leave the field (a Save that was held). */
  showHint: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  'aria-label': PropTypes.string,
};
